import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- Enum定義 ---

/** チケット分類（TASK=タスクチケット / APPROVAL=承認チケット） */
export const TicketCategory = {
  TASK: 'タスク',
  APPROVAL: '承認',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TicketCategory = keyof typeof TicketCategory;

/** スコープ（GLOBAL=全体 / PRIVATE=個人TODO） */
export const TicketScope = {
  GLOBAL: 'グローバル',
  PRIVATE: 'プライベート',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TicketScope = keyof typeof TicketScope;

// --- 型定義 ---

/** 種別一覧の1件（API7001） */
export type TicketTypeSummaryResult = {
  typeId: string;
  name: string;
  ticketCategory: TicketCategory;
  scope: TicketScope;
  keyPrefix: string | null;
  isActive: boolean;
  /** 一度でも有効化したか（物理削除可否・無効化状態の判定に使用） */
  hasBeenActivated: boolean;
  sortOrder: number;
  ownerUserId: string | null;
};

/** フォーム選択肢（API7002） */
export type TicketFormSelectItemResult = {
  selectItemId: string;
  code: string;
  name: string;
  sortOrder: number;
  deleted: boolean;
};

/** フォーム項目（API7002） */
export type TicketFormItemResult = {
  itemId: string;
  itemKey: string;
  label: string;
  formType: string;
  sortOrder: number;
  deleted: boolean;
  selectItems: TicketFormSelectItemResult[];
};

/** フォームグループ（API7002） */
export type TicketFormGroupResult = {
  groupId: string;
  name: string;
  sortOrder: number;
  items: TicketFormItemResult[];
};

/** 種別取得APIレスポンス（API7002） */
export type GetTicketTypeResponse = {
  typeId: string;
  name: string;
  ticketCategory: TicketCategory;
  scope: TicketScope;
  keyPrefix: string | null;
  isActive: boolean;
  hasBeenActivated?: boolean;
  ownerUserId: string | null;
  results: TicketFormGroupResult[];
  unassignedItems: TicketFormItemResult[];
};

// --- リクエスト型 ---

/** 種別一覧取得（API7001） */
export type ListTicketTypeParams = {
  activeOnly?: boolean;
};

/** 種別取得（API7002） */
export type GetTicketTypeParams = {
  typeId: string;
  visibleOnly?: boolean;
};

/** 種別登録（API7003） */
export type CreateTicketTypeParams = {
  name: string;
  scope: TicketScope;
  ticketCategory: TicketCategory;
  keyPrefix?: string;
};

/** 種別更新（API7004） */
export type UpdateTicketTypeParams = {
  typeId: string;
  name?: string;
  scope?: TicketScope;
  ticketCategory?: TicketCategory;
  keyPrefix?: string;
  sortOrder?: number;
};

/** 種別削除（API7005） */
export type DeleteTicketTypeParams = {
  typeId: string;
};

/** 種別有効化/無効化（API7006） */
export type ActiveTicketTypeParams = {
  typeId: string;
  active: boolean;
};

/** フォームグループ登録（API7011） */
export type CreateTicketFormGroupParams = {
  typeId: string;
  name: string;
};

/** フォームグループ編集（API7012） */
export type UpdateTicketFormGroupParams = {
  groupId: string;
  name: string;
};

/** フォームグループ表示設定（API7014） */
export type UpdateTicketFormGroupDisplayParams = {
  typeId: string;
  groups: { groupId: string; sortOrder: number }[];
};

/** フォーム選択肢入力（登録） */
export type CreateTicketFormSelectItemInput = {
  code: string;
  name: string;
  sortOrder: number;
};

/** フォームアイテム登録（API7021） */
export type CreateTicketFormItemParams = {
  typeId: string;
  itemKey: string;
  label: string;
  formType: string;
  groupId?: string;
  selectItems?: CreateTicketFormSelectItemInput[];
};

/** フォーム選択肢入力（編集） */
export type UpdateTicketFormSelectItemInput = {
  selectItemId?: string | null;
  code: string;
  name: string;
  sortOrder: number;
  isDeleted: boolean;
};

/** フォームアイテム編集（API7022） */
export type UpdateTicketFormItemParams = {
  itemId: string;
  label: string;
  selectItems?: UpdateTicketFormSelectItemInput[];
};

/** フォームアイテム削除（API7023） */
export type DeleteTicketFormItemParams = {
  itemId: string;
};

/** フォームアイテム表示設定（API7024） */
export type UpdateTicketFormItemDisplayParams = {
  typeId: string;
  items: { itemId: string; groupId?: string | null; sortOrder: number }[];
};

// --- 共通ヘッダー ---
const authHeader = () => ({
  headers: { 'X-Session-Id': localStorage.getItem('session-id') },
});

const handleError = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response;
    throw createApiError(status, data?.message || error.message);
  }
  throw new ServerError();
};

// --- API関数（種別） ---

/**
 * チケット種別一覧取得API (API7001)
 * @param params.activeOnly - 有効のみ（任意, trueで is_active=true のみ）
 */
export const listTicketType = async (
  params: ListTicketTypeParams = {},
): Promise<TicketTypeSummaryResult[]> => {
  try {
    const response = await axios.post(KanzhongBackend.LIST_TICKET_TYPE, params, authHeader());
    return response.data.results;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * チケット種別取得API (API7002)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.visibleOnly - 表示中のみ（任意, trueで sort_order>=0 のグループのみ）
 */
export const getTicketType = async (
  params: GetTicketTypeParams,
): Promise<GetTicketTypeResponse> => {
  try {
    const response = await axios.post(KanzhongBackend.GET_TICKET_TYPE, params, authHeader());
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * チケット種別登録API (API7003)
 * @param params.name - 種別名（必須, 最大200文字）
 * @param params.scope - スコープ（必須, GLOBAL/PRIVATE）
 * @param params.ticketCategory - チケット分類（必須, TASK/APPROVAL）
 * @param params.keyPrefix - キープレフィックス（GLOBAL時必須, 英字最大5・PRV/REQ/CONT禁止）
 */
export const createTicketType = async (params: CreateTicketTypeParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_TICKET_TYPE, params, authHeader());
    return response.data.typeId;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * チケット種別更新API (API7004)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.name - 種別名（任意, 最大200文字）
 * @param params.scope / params.ticketCategory / params.keyPrefix - 未有効化時のみ変更可
 * @param params.sortOrder - 表示順（任意）
 */
export const updateTicketType = async (params: UpdateTicketTypeParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TICKET_TYPE, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * チケット種別削除API (API7005)
 * @param params.typeId - 種別ID（必須, 最大64文字。未有効化のみ削除可）
 */
export const deleteTicketType = async (params: DeleteTicketTypeParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_TICKET_TYPE, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * チケット種別有効化/無効化API (API7006)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.active - 有効化（必須, true=有効化 / false=無効化）
 */
export const activeTicketType = async (params: ActiveTicketTypeParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.ACTIVE_TICKET_TYPE, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

// --- API関数（フォームテンプレート） ---

/**
 * フォームグループ登録API (API7011)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.name - グループ名（必須, 最大200文字）
 */
export const createTicketFormGroup = async (
  params: CreateTicketFormGroupParams,
): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_TICKET_FORM_GROUP,
      params,
      authHeader(),
    );
    return response.data.groupId;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * フォームグループ編集API (API7012)
 * @param params.groupId - グループID（必須, 最大64文字）
 * @param params.name - グループ名（必須, 最大200文字）
 */
export const updateTicketFormGroup = async (params: UpdateTicketFormGroupParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TICKET_FORM_GROUP, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * フォームグループ表示設定API (API7014)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.groups - 表示するグループの一覧（含まれないものは非表示に設定）
 */
export const updateTicketFormGroupDisplay = async (
  params: UpdateTicketFormGroupDisplayParams,
): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TICKET_FORM_GROUP_DISPLAY, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * フォームアイテム登録API (API7021)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.itemKey - 項目キー（必須, 最大100文字, 半角英数字 + - + _ のみ）
 * @param params.label - ラベル（必須, 最大200文字）
 * @param params.formType - フォーム種別（必須, master_type: form_type）
 * @param params.groupId - グループID（任意, 未所属はnull）
 * @param params.selectItems - 選択肢リスト（選択系formTypeの場合）
 */
export const createTicketFormItem = async (params: CreateTicketFormItemParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_TICKET_FORM_ITEM,
      params,
      authHeader(),
    );
    return response.data.itemId;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * フォームアイテム編集API (API7022)
 * @param params.itemId - 項目ID（必須, 最大64文字）
 * @param params.label - ラベル（必須, 最大200文字）
 * @param params.selectItems - 選択肢リスト（選択系formTypeの場合）
 */
export const updateTicketFormItem = async (params: UpdateTicketFormItemParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TICKET_FORM_ITEM, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * フォームアイテム削除API (API7023)
 * @param params.itemId - 項目ID（必須, 最大64文字。グループ所属中は削除不可）
 */
export const deleteTicketFormItem = async (params: DeleteTicketFormItemParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_TICKET_FORM_ITEM, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};

/**
 * フォームアイテム表示設定API (API7024)
 * @param params.typeId - 種別ID（必須, 最大64文字）
 * @param params.items - 更新対象の全item（groupId=nullで未所属・sortOrder=-1）
 */
export const updateTicketFormItemDisplay = async (
  params: UpdateTicketFormItemDisplayParams,
): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TICKET_FORM_ITEM_DISPLAY, params, authHeader());
  } catch (error) {
    handleError(error);
  }
};
